import React, { FormEvent, useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Button } from "../Button";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import {
  generateKioskPairingCode,
  getS710Devices,
  unpairS710Device,
} from "../../services/KioskService";
import { Kiosk, S710Device } from "../../types/kiosk.type";

interface S710DevicesModalProps {
  open: boolean;
  kiosk: Pick<Kiosk, "kioskId" | "name"> | null;
  onClose: () => void;
}

function formatDate(value?: string | null): string {
  if (!value) return "Not yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not available" : date.toLocaleString();
}

const S710DevicesModal: React.FC<S710DevicesModalProps> = ({ open, kiosk, onClose }) => {
  const [devices, setDevices] = useState<S710Device[]>([]);
  const [deviceName, setDeviceName] = useState("");
  const [pairingCode, setPairingCode] = useState<{ value: string; expiresAt: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [creatingCode, setCreatingCode] = useState(false);
  const [unpairTarget, setUnpairTarget] = useState<S710Device | null>(null);
  const [unpairingId, setUnpairingId] = useState<string | null>(null);

  const loadDevices = useCallback(async () => {
    if (!kiosk) return;
    setLoading(true);
    try {
      setDevices(await getS710Devices(kiosk.kioskId));
      setLoadError("");
    } catch (error) {
      const message = (error as Error).message || "Failed to load S710 devices";
      setLoadError(message);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }, [kiosk]);

  useEffect(() => {
    if (open) {
      void loadDevices();
    } else {
      setPairingCode(null);
      setDeviceName("");
      setUnpairTarget(null);
    }
  }, [open, loadDevices]);

  if (!open || !kiosk) return null;

  const handleGenerateCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = deviceName.trim();
    if (!name) {
      showErrorToast("Enter a name for this S710 device");
      return;
    }

    setCreatingCode(true);
    try {
      const generated = await generateKioskPairingCode(kiosk.kioskId, name);
      setPairingCode({ value: generated.pairingCode, expiresAt: generated.expiresAt });
      setDeviceName("");
      await loadDevices();
      showSuccessToast("S710 activation code generated");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to generate pairing code");
    } finally {
      setCreatingCode(false);
    }
  };

  const handleUnpair = async () => {
    if (!unpairTarget) return;
    const target = unpairTarget;
    setUnpairingId(target.deviceId);
    try {
      await unpairS710Device(kiosk.kioskId, target.deviceId);
      setUnpairTarget(null);
      await loadDevices();
      showSuccessToast(`${target.deviceName} unpaired`);
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to unpair S710 device");
    } finally {
      setUnpairingId(null);
    }
  };

  const handleCopyCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode.value);
      showSuccessToast("Pairing code copied");
    } catch {
      showErrorToast("Could not copy the pairing code. Please copy it manually.");
    }
  };

  const handleClose = () => {
    if (creatingCode || unpairingId) return;
    setPairingCode(null);
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[1px]"
      onClick={handleClose}
      role="presentation"
    >
      <section
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s710-devices-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="s710-devices-title" className="text-xl font-semibold text-slate-900 dark:text-gray-100">
              S710 devices · {kiosk.kioskId}
            </h2>
          </div>
          <button type="button" className="text-slate-500 hover:text-slate-900 dark:hover:text-white" onClick={handleClose} aria-label="Close" disabled={creatingCode || Boolean(unpairingId)}>
            ×
          </button>
        </div>

        <form onSubmit={(event) => void handleGenerateCode(event)} className="mt-5 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
          <label htmlFor="s710-device-name" className="mb-1 block text-sm font-medium text-slate-700 dark:text-gray-200">
            Device name
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id="s710-device-name"
              value={deviceName}
              onChange={(event) => setDeviceName(event.target.value)}
              maxLength={100}
              placeholder="e.g. Front counter"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
            />
            <Button type="submit" variant="primary" disabled={creatingCode || !deviceName.trim()}>
              {creatingCode ? "Generating…" : "Generate Pairing Code"}
            </Button>
          </div>
        </form>

        {pairingCode && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700/40 dark:bg-amber-900/20">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Save or print this code. It will not be shown again.
            </p>
            <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <code className="break-all text-lg font-bold tracking-wider text-amber-950 dark:text-amber-100">
                {pairingCode.value}
              </code>
              <Button variant="secondary" onClick={() => void handleCopyCode()}>
                Copy code
              </Button>
            </div>
            <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">
              Valid until this device is unpaired. Existing paired devices remain active.
            </p>
          </div>
        )}

        <div className="mt-6">
          <h3 className="font-semibold text-slate-900 dark:text-gray-100">Device history</h3>
          {loading ? (
            <p className="py-5 text-sm text-slate-500 dark:text-gray-400">Loading devices…</p>
          ) : loadError ? (
            <div className="py-5 text-sm text-red-700 dark:text-red-300">
              <p>{loadError}</p>
              <button type="button" className="mt-2 underline" onClick={() => void loadDevices()}>
                Retry loading devices
              </button>
            </div>
          ) : devices.length === 0 ? (
            <p className="py-5 text-sm text-slate-500 dark:text-gray-400">No S710 devices have been paired yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-200 dark:divide-slate-700">
              {devices.map((device) => (
                <li key={device.deviceId} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900 dark:text-gray-100">{device.deviceName}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        device.status === "PAIRED"
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                          : device.status === "PENDING"
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-gray-200"
                      }`}>
                        {device.status.toLowerCase()}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                      {device.status === "PAIRED"
                        ? `Paired ${formatDate(device.pairedAt)} · Last token refresh ${formatDate(device.lastUsedAt)}`
                        : device.status === "PENDING"
                          ? "Activation code has not been used"
                          : device.revokedAt
                            ? `Unpaired ${formatDate(device.revokedAt)}`
                            : `Created ${formatDate(device.pairingCodeCreatedAt)}`}
                    </p>
                  </div>
                  {device.status !== "UNPAIRED" && (
                    <button
                      type="button"
                      className="self-start rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-700/50 dark:text-red-300 dark:hover:bg-red-900/20 sm:self-center"
                      disabled={Boolean(unpairingId)}
                      onClick={() => setUnpairTarget(device)}
                    >
                      {device.status === "PAIRED" ? "Unpair" : "Cancel code"}
                    </button>
                  )}
                  {unpairTarget?.deviceId === device.deviceId && (
                    <div className="basis-full rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-700/40 dark:bg-red-900/20">
                      <p className="text-sm text-red-800 dark:text-red-200">
                        {device.status === "PAIRED"
                          ? device.legacy
                            ? `${device.deviceName}'s refresh credentials will be revoked. Access tokens issued before device tracking may remain valid until they expire. Other devices are unaffected.`
                            : `${device.deviceName} will immediately lose access. Other S710 devices will keep working.`
                          : "This pairing code will stop working"}
                      </p>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setUnpairTarget(null)} disabled={Boolean(unpairingId)}>
                          Keep device
                        </Button>
                        <Button variant="primary" onClick={() => void handleUnpair()} disabled={Boolean(unpairingId)}>
                          {unpairingId === device.deviceId ? "Unpairing…" : "Confirm"}
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <Button variant="secondary" onClick={handleClose} disabled={creatingCode || Boolean(unpairingId)}>Close</Button>
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default S710DevicesModal;
